// BruchtestDisplayPage.jsx — Vollbild-Anzeige fuer zweiten Bildschirm / Beamer.
//
// Bleibt eine eigene Seite: sie zeigt je Athlet eine Technikliste, die es auf
// keiner anderen Leinwand gibt. Raster, Farben, Kopfband, Fussleiste und
// Ranglistenspalte kommen aber aus styles/leinwand.css -- dieselben wie bei
// Formen, Siegerehrung und Sieger.
import React, { useState, useEffect } from 'react';
import '../styles/leinwand.css';
import './BruchtestDisplayPage.css';
// Platzabzeichen statt 🥇🥈🥉 — dieselben wie auf den anderen Leinwaenden.
import Platz from '../components/leinwand/Platz';

const CHANNEL = 'tda-bruchtest';
const LS_KEY  = 'tda-bruchtest-state';

export default function BruchtestDisplayPage() {
  const [state, setState]             = useState(null);
  const [animKey, setAnimKey]         = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Overflow verstecken
  useEffect(() => {
    const prev = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow = prev.body;
    };
  }, []);

  // Auto-Vollbild beim Öffnen
  useEffect(() => {
    const t = setTimeout(() => {
      document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen?.({ navigationUI: 'hide' }).catch(() => {});
  };

  // Externe Elemente (Chat-Widget etc.) ausblenden
  useEffect(() => {
    const hide = () => {
      document.body.querySelectorAll('body > *:not(#root)').forEach(el => {
        if (!['SCRIPT','STYLE','LINK','META'].includes(el.tagName))
          el.style.setProperty('display', 'none', 'important');
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  // BroadcastChannel + localStorage lauschen
  useEffect(() => {
    const apply = (raw) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setState(parsed || null);
        setAnimKey(k => k + 1);
      } catch {}
    };

    apply(localStorage.getItem(LS_KEY));

    const onStorage = (e) => { if (e.key === LS_KEY) apply(e.newValue); };
    window.addEventListener('storage', onStorage);

    let ch = null;
    try {
      ch = new BroadcastChannel(CHANNEL);
      ch.onmessage = (e) => apply(e.data);
    } catch {}

    return () => {
      window.removeEventListener('storage', onStorage);
      ch?.close();
    };
  }, []);

  if (!isFullscreen) {
    return <SetupOverlay onFullscreen={toggleFullscreen} />;
  }

  const isScoring = state?.type === 'scoring' && state?.currentTeilnehmer;
  const isWinner  = state?.type === 'winner'  && state?.ranking?.length > 0;
  return isWinner
    ? <WinnerScreen key={animKey} data={state} onFullscreen={toggleFullscreen} />
    : isScoring
      ? <ScoringScreen key={animKey} data={state} onFullscreen={toggleFullscreen} />
      : <StandbyScreen data={state} onFullscreen={toggleFullscreen} />;
}

// ── Setup-Overlay ─────────────────────────────────────────────────────────
function SetupOverlay({ onFullscreen }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/bruchtest-display`;
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <div className="btd-setup">
      <div className="btd-setup-icon">🧱</div>
      <div className="btd-setup-card">
        <div className="btd-setup-title">Bruchtest-Anzeige einrichten</div>
        <div className="btd-setup-option">
          <div className="btd-setup-option-label">Option A — Fenster verschieben</div>
          <p className="btd-setup-hint">Fenster an den Bildschirmrand ziehen → auf Monitor 2 → Vollbild starten</p>
          <button className="btd-setup-btn" onClick={onFullscreen}>⛶ Vollbild starten</button>
        </div>
        <div className="btd-setup-divider">oder</div>
        <div className="btd-setup-option">
          <div className="btd-setup-option-label">Option B — URL direkt öffnen</div>
          <div className="btd-setup-url">{url}</div>
          <button className="btd-setup-copy" onClick={copy}>{copied ? 'Kopiert' : 'Adresse kopieren'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Standby-Screen ────────────────────────────────────────────────────────
function StandbyScreen({ data, onFullscreen }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="lw btd">
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Bruchtest</div>
          {data?.kategorie && <div className="lw-kopf-kat">{data.kategorie}</div>}
        </div>
        <div className="lw-kopf-titel">破 BRUCHTEST</div>
        <div className="lw-kopf-status wartet">
          {time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      <div className="lw-buehne">
        <div className="lw-kanji wasserzeichen" aria-hidden>破</div>
        <div className="lw-name">Bruchtest</div>
        <div className="lw-bereit-text">BEREIT</div>
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Tiger &amp; Dragon Association</div>
        <button className="lw-vollbild" onClick={onFullscreen} title="Vollbild">⛶ Vollbild</button>
      </div>
    </div>
  );
}

// ── Winner-Screen ─────────────────────────────────────────────────────────
function WinnerScreen({ data, onFullscreen }) {
  const { ranking = [], kategorie } = data;
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);

  const top3 = ranking.slice(0, 3);
  const rest = ranking.slice(3);

  return (
    <div className={`lw btd${visible ? ' btd--sichtbar' : ''}`}>
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Bruchtest</div>
          {kategorie && <div className="lw-kopf-kat">{kategorie}</div>}
        </div>
        <div className="lw-kopf-titel">勝 ERGEBNIS</div>
        <div className="lw-kopf-status">FERTIG</div>
      </div>

      <div className="lw-buehne">
      <div className="lw-kanji wasserzeichen" aria-hidden>勝</div>

      {/* Dasselbe Podest wie bei der Siegerehrung (styles/leinwand.css):
          eine Saeule je Platz, gestufte Sockel, gemeinsame Standlinie.
          Die Reihenfolge 2-1-3 macht das Stylesheet ueber `order`. */}
      <div className="lw-podest">
        {[0, 1, 2].map(i => {
          const r = top3[i];
          const rang = i + 1;
          return (
            <div key={r?.anmeldung_id ?? `leer-${rang}`}
                 className={`lw-podest-saeule lw-podest-saeule--${rang}${r ? '' : ' leer'}`}
                 style={{ '--delay': `${0.05 + i * 0.15}s` }}>
              <div className="lw-podest-person">
                <div className="btd-winner-medal"><Platz platz={rang} gross /></div>
                {r ? (
                  <>
                    <div className="lw-podest-name">
                      <span className="lw-podest-vorname">{r.vorname}</span>
                      <span className="lw-podest-nachname">{r.nachname}</span>
                    </div>
                    {r.verein_name && <div className="lw-podest-verein">{r.verein_name}</div>}
                    <div className="lw-podest-punkte">{r.gesamtPunkte.toFixed(2)}</div>
                  </>
                ) : (
                  <div className="btd-winner-leer">{rang}. Platz</div>
                )}
              </div>
              <div className="lw-podest-sockel"><span>{rang}</span></div>
            </div>
          );
        })}
      </div>

      {rest.length > 0 && (
        <div className="btd-winner-rest">
          {rest.map((r, i) => (
            <div key={r.anmeldung_id} className="btd-winner-rest-item">
              <span className="btd-winner-rest-pos">{i + 4}.</span>
              <span className="btd-winner-rest-name">{r.vorname} {r.nachname}</span>
              <span className="btd-winner-rest-club">{r.verein_name}</span>
              <span className="btd-winner-rest-pts">{r.gesamtPunkte.toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Sieger</div>
        <div className="lw-fuss-wert">
          {top3[0] ? `${top3[0].vorname} ${top3[0].nachname}` : ''}
        </div>
        <button className="lw-vollbild" onClick={onFullscreen} title="Vollbild">⛶</button>
      </div>
    </div>
  );
}

// ── Scoring-Screen ────────────────────────────────────────────────────────
function ScoringScreen({ data, onFullscreen }) {
  const { currentTeilnehmer, kategorie, modus, techniken = [], gesamtpunkte = 0, ranking = [], nextTeilnehmer = null } = data;

  return (
    <div className="lw btd mit-leiste">
      <div className="lw-kopf">
        <div className="lw-kopf-links">
          <div className="lw-kopf-turnier">Bruchtest</div>
          <div className="lw-kopf-kat">
            {kategorie}{modus ? ` · ${modus}` : ''}
          </div>
        </div>
        <div className="lw-kopf-titel">破 BRUCHTEST</div>
        <div className="lw-kopf-status">LIVE</div>
      </div>

      {/* ── Buehne: Athlet, Techniken, Summe ── */}
      <div className="lw-buehne">
        <div className="lw-name">
          {currentTeilnehmer.vorname} {currentTeilnehmer.nachname}
        </div>
        {currentTeilnehmer.verein_name && (
          <div className="lw-unterzeile">
            <span className="lw-verein">{currentTeilnehmer.verein_name}</span>
          </div>
        )}

        <div className="btd-techniken-list">
          {techniken.length === 0 ? (
            <div className="btd-no-techniken">Noch keine Technik bewertet</div>
          ) : techniken.map((t, i) => (
            <div key={i} className={`btd-technik-item ${t.erfolg ? '' : 'fail'}`}>
              <span className="btd-technik-nr">{i + 1}</span>
              <div className="btd-technik-info">
                <span className="btd-technik-name">
                  {t.technikName || t.technikId}
                  {t.mitSprung && <span className="btd-sprung-tag">Sprung</span>}
                </span>
                <span className="btd-technik-sub">
                  {t.anzahl}× {t.materialName}
                  {t.versuche > 1 && ` · ${t.versuche} Versuche`}
                </span>
              </div>
              <span className="btd-technik-punkte">
                {t.erfolg ? t.punkte?.toFixed(1) : '—'}
              </span>
            </div>
          ))}
        </div>

        <div className="btd-total-row">
          <span className="btd-total-label">Gesamt</span>
          <span className="btd-total-value">{gesamtpunkte.toFixed(2)}</span>
        </div>
      </div>

      {/* ── Ranglistenspalte ── */}
      <div className="lw-leiste">
        <div className="lw-leiste-titel">順位 Rangliste</div>
        {ranking.map((r, idx) => {
          const istAktuell = r.anmeldung_id === currentTeilnehmer.anmeldung_id;
          return (
            <div key={r.anmeldung_id} className={`lw-leiste-zeile${istAktuell ? ' aktuell' : ''}`}>
              <Platz platz={idx + 1} />
              <span className="lw-leiste-name">{r.vorname} {r.nachname}</span>
              <span className="lw-leiste-punkte">
                {r.gesamtPunkte > 0 ? r.gesamtPunkte.toFixed(1) : '—'}
              </span>
            </div>
          );
        })}
      </div>

      <div className="lw-fuss">
        <div className="lw-fuss-lbl">Nächster</div>
        <div className="lw-fuss-wert">
          {nextTeilnehmer
            ? `${nextTeilnehmer.vorname} ${nextTeilnehmer.nachname}${nextTeilnehmer.verein_name ? ' · ' + nextTeilnehmer.verein_name : ''}`
            : '—'}
        </div>
        <button className="lw-vollbild" onClick={onFullscreen} title="Vollbild">⛶</button>
      </div>
    </div>
  );
}
